import React, { useEffect, useState } from 'react'
import request from 'axios'
import { useRouter } from 'next/router'
import InputGroup from './elements/InputGroup'
import { SimpleButton as Button, ButtonTypeEnum } from '../shared/SimpleButton'
import { useFormik } from 'formik'
import * as Yup from 'yup'

export const ERRORS = {
  ORIGIN_ZIP_ERROR: 'North Park Transportation does not service this origin.',
  DEST_ZIP_ERROR:
    'North Park Transportation does not service this destination.',
}

export type TerminalContact = {
  id: string
  number: number
  name: string
  line1: string
  line2: string
  city: string
  state: string
  zip: number
  phone: string
}

export type EstimateResult = {
  deliveryDate: string
  destinationTerminalContact: TerminalContact
  error: string
  firstAvailablePickupDate: string
  holidayDays: number
  originTerminalContact: TerminalContact
  serviceDays: number
  statusCode: number
  weekendDays: number
  destinationServicePointServiceString: string
  originServicePointServiceString: string
}

export type TransitDetails = {
  pickupZip: string
  deliveryZip: string
  pickupDate: string
}

export interface ServicePointServiceProps {
  city: string
  state: string
  zipCode: string
}

const isZipError = (error: string) => {
  const { ORIGIN_ZIP_ERROR, DEST_ZIP_ERROR } = ERRORS
  return error === ORIGIN_ZIP_ERROR || error === DEST_ZIP_ERROR
}

const getTransitTime = async (
  transitDetails: TransitDetails
): Promise<EstimateResult | void> => {
  const data = { ...transitDetails, includeContacts: true }
  const errorMsg = 'Error calculating transit time'

  try {
    const response = await request.post(
      '/api/internal-api/get-transit-time',
      { data },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    const notZipCodeError =
      response.status !== 200 || (response.data.error && !isZipError)

    if (notZipCodeError) {
      throw new Error(errorMsg)
    }

    return { ...response.data, submittedTransitDetails: transitDetails }
  } catch (error: any) {
    throw new Error(errorMsg)
  }
}

const zipCodeValidation = Yup.string()
  .matches(/^\d{5}$/, 'Please enter a valid 5 digit zip code')
  .required('Required')

const today = new Date()
today.setHours(0, 0, 0, 0)

const validationSchema = Yup.object().shape({
  pickupZip: zipCodeValidation,
  deliveryZip: zipCodeValidation,
  pickupDate: Yup.date()
    .min(new Date(today), 'Date cannot be in the past')
    .required('Required'),
})

interface TransitTimeEstimateFormProps {
  isTransitTimeCalculatorPage: boolean
  loading: boolean
  setLoading: (value: boolean) => void
  setTransitTimeResult: (values: any) => void | Promise<void>
}

export const TransitTimeEstimateForm: React.FunctionComponent<
  TransitTimeEstimateFormProps
> = ({
  isTransitTimeCalculatorPage,
  loading,
  setLoading,
  setTransitTimeResult,
}) => {
  const [error, setError] = useState('')

  const router = useRouter()
  const query = router.query as TransitDetails

  const validateQueryParams = (query: TransitDetails) => {
    const expectedKeys = ['pickupZip', 'deliveryZip', 'pickupDate']
    return expectedKeys.every((key: string) => key in query)
  }

  useEffect(() => {
    /*
    Calculator form submissions can be done from within service-area page or 
    transit-time-calculator page. This logic sets transit time result after redirect 
    from service-area page to avoid persisting state as a result of re-renders.
    */
    if (!router.isReady) return

    const setResult = async () => {
      setError('')
      setLoading(true)

      try {
        const result = await getTransitTime(query)
        const pickUpZip = query.pickupZip
        const deliveryZip = query.deliveryZip
        setTransitTimeResult({ pickUpZip, deliveryZip, ...result })
      } catch (error: any) {
        setError(error.message)
      }
      setLoading(false)
    }

    const isValid = validateQueryParams(query)

    if (isValid && isTransitTimeCalculatorPage) {
      setResult()
    }
  }, [router.isReady])

  const todayDate = new Date().toISOString().slice(0, 10)

  const initialValues = {
    pickupZip: query.pickupZip || '',
    deliveryZip: query.deliveryZip || '',
    pickupDate: query.pickupDate || todayDate,
  } as TransitDetails

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values: TransitDetails) => {
      setError('')
      setLoading(true)
      try {
        if (!isTransitTimeCalculatorPage) {
          router.push({
            pathname: '/transit-time-calculator',
            query: values,
          })
        } else {
          router.replace({ query: { ...values } })
          const result = await getTransitTime(values)
          setTransitTimeResult(result)
        }

        setLoading(false)
      } catch (error: any) {
        setError(error.message)
        setLoading(false)
      }
    },
  })

  const isDisabled = !formik.isValid || loading
  const formHasErrors = Object.keys(formik.errors).length > 0

  const buttonText = isTransitTimeCalculatorPage
    ? 'Get More Transit Times'
    : 'Get Transit Times'

  const formContainerStyles = isTransitTimeCalculatorPage
    ? 'flex-col xsm:justify-start md:flex-row md:justify-end md:flex-wrap md:justify-center md:items-end'
    : 'flex-col xsm:justify-center sm:flex-row lgMd:justify-end flex-wrap'

  const formFieldStyles = isTransitTimeCalculatorPage
    ? 'shrink-0 sm:grow md:self-baseline md:mr-6 mdXl:grow-0 lg:min-w-[212px]'
    : 'shrink-0 xsm:grow sm:mr-6 md:grow lgMd:grow-0 xlMd:min-w-[212px]'

  const lastFormFieldStyle = isTransitTimeCalculatorPage
    ? 'shrink-0 sm:grow md:self-baseline mdLg:mr-6 mdXl:grow-0 lg:mr-6 lg:min-w-[212px]'
    : 'shrink-0 xsm:grow md:mr-0 md2xl:mr-6 lgMd:mr-0 lgMd:grow-0 xl:min-w-[212px]'

  const submitButtonContainerStyles = isTransitTimeCalculatorPage
    ? 'flex flex-col shrink-0 mt-6 xsm:mt-6 sm:grow-0 md:ml-auto md:basis-[28%] mdLg:basis-[0%] mdXl:ml-0 lg:basis-0'
    : 'flex flex-col mt-6 shrink-0 sm:ml-auto md:basis-[40%] md2xl:basis-[0%] mdLg:w-fit lgMd:basis-[60%] md:self-end 2xl:basis-[100%]'

  return (
    <form className="w-full" onSubmit={formik.handleSubmit}>
      <div className={`flex ${formContainerStyles}`}>
        <div className={formFieldStyles}>
          <InputGroup
            additionalContainerClasses="md:!mb-0"
            label="Origin Zip Code"
            placeholder=""
            error={formik.errors.pickupZip}
            value={formik.values.pickupZip}
            touched={formik.touched.pickupZip}
            handleBlur={formik.handleBlur}
            handleChange={formik.handleChange}
            name="pickupZip"
          />
        </div>

        <div className={formFieldStyles}>
          <InputGroup
            additionalContainerClasses="md:!mb-0"
            label="Destination Zip Code"
            placeholder=""
            error={formik.errors.deliveryZip}
            value={formik.values.deliveryZip}
            touched={formik.touched.deliveryZip}
            handleBlur={formik.handleBlur}
            handleChange={formik.handleChange}
            name="deliveryZip"
          />
        </div>
        <div className={lastFormFieldStyle}>
          <InputGroup
            additionalContainerClasses="md:!mb-0"
            label="Pickup Date"
            placeholder=""
            type="date"
            error={formik.errors.pickupDate}
            value={formik.values.pickupDate}
            touched={formik.touched.pickupDate}
            handleBlur={formik.handleBlur}
            handleChange={formik.handleChange}
            name="pickupDate"
          />
        </div>
        <div className={submitButtonContainerStyles}>
          <Button
            additionalStyles="leading-[22px]"
            buttonText={buttonText}
            isDisabled={isDisabled}
            type={ButtonTypeEnum.submit}
          />
          {(error || formHasErrors) && (
            <p className="flex h-3 mt-1 text-xs text-red-brand">{error}</p>
          )}
        </div>
      </div>
    </form>
  )
}
